import type { FormUser } from './validationSchema';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Checkbox, Modal, TextField, useTheme } from '@mui/material';
import { observer } from 'mobx-react';
import { Button } from '@components/index';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { UserGroupSelect } from '@components/userGroupSelect/UserGroupSelect';
import { useStore } from '@stores/RootStore';
import { BulletIcon } from '@icons/BulletIcon';
import { XIcon } from '@icons/XIcon';
import { AddIcon, CheckboxIcon, DashIcon } from '@icons/index';
import useDynamicInput from '@customHooks/useDynamicInput';
import { useScrollToBottom } from '@customHooks/useScrollToBottom';
import { useGetTenantMembers } from '@queryHooks/useTenant';
import { useInviteUserToTenant } from '@queryHooks/useUser';
import type { Page } from '@type/Page';
import type { TenantMemberSimpleModel } from '@repositories/tenantMemberRepository/models/TenantMemberSimpleModel';
import * as S from './UserIndividualRegisterStyles';
import { useUserForm } from './validationSchema';

export const UserIndividualRegister = observer(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    uiStore: { userIndividualRegisterStore: modal },
    authStore,
  } = useStore();
  const [account, setAccount] = useState<string>();
  const [bodyHeight, setBodyHeight] = useState(0);
  const { scrollToBottom, scrollRef } = useScrollToBottom();

  const {
    data: dynamicGroups,
    add,
    handleChangeValue,
    remove,
  } = useDynamicInput<{ userGroupId: string; name: string }>({
    userGroupId: authStore.managerTenantInfo.id,
    name: authStore.managerTenantInfo.name,
  });

  // NOTE. 이미 테넌트 맴버인지 체크
  useGetTenantMembers(
    authStore.curTenant.id,
    { keyword: account },
    {
      enabled: !!account,
      onSuccess: (data: Page<TenantMemberSimpleModel>) => {
        if (!data.empty && data.content.find((member: TenantMemberSimpleModel) => member.email === account)) {
          setError('email', { message: t('Member_User_Already_Joined') });
        }
      },
      cacheTime: 0,
      staleTime: 0,
    },
  );

  const hookForm = useUserForm();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    control,
    watch,
  } = hookForm;

  const isMailSend = watch('isMailSend');

  const validation = {
    success: (data: FormUser) => {
      inviteUser(
        {
          email: data.email,
          phone: data.phone,
          customRole: data.customRole,
          username: data.username,
          userGroupIdList: dynamicGroups.filter(group => !!group.userGroupId).map(selected => selected.userGroupId),
          tenantId: authStore.curTenant.id,
          isMailSend: data.isMailSend ?? true,
          isExistingUserUpdated: true,
        },
        {
          onSuccess: () => {
            modal.close();
          },
        },
      );
    },
  };

  const handleSeleted = (idx: number) => (e: React.MouseEvent<HTMLLIElement>) => {
    if (e.currentTarget.dataset.name !== undefined && e.currentTarget.dataset.userGroupId !== undefined) {
      handleChangeValue(
        {
          name: e.currentTarget.dataset.name,
          userGroupId: e.currentTarget.dataset.userGroupId,
        },
        idx,
      );
    }
  };

  const handleChangeEmail =
    (onChange: (...event: any[]) => void) => (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      onChange(e.target.value);
      setAccount(e.target.value);
    };

  const { mutate: inviteUser, isPending } = useInviteUserToTenant();

  useEffect(() => {
    scrollToBottom();
    if (scrollRef.current !== null) {
      setBodyHeight(scrollRef.current.clientHeight);
    } else {
      setBodyHeight(prev => prev + 1);
    }
  }, [scrollRef, scrollToBottom]);

  return (
    <Modal open={modal.visible}>
      <S.ModalWrapper onSubmit={handleSubmit(validation.success)}>
        <S.Header>
          <S.HeaderTitle>{t('Member_User_12')}</S.HeaderTitle>
          <Button onClick={modal.close}>
            <XIcon color={theme.colors['ic-gray-main']} />
          </Button>
        </S.Header>
        <S.Body ref={scrollRef}>
          <div className="info">
            <S.EssentialInfo>
              <div className="infoTitle">{t('Member_User_Create_01')}</div>
              <S.InfoKey>
                {t('Workflow_Main_05')}
                <span>*</span>
              </S.InfoKey>
              <TextField
                {...register('username')}
                size="medium"
                fullWidth
                placeholder={t('Member_User_Bulk_Create_16')}
                sx={{ marginBottom: '18px' }}
                error={!!errors.username?.message}
                helperText={errors.username?.message}
              />
              <S.InfoKey>
                {t('Member_User_14')}
                <span>*</span>
              </S.InfoKey>
              <Controller
                control={control}
                name="email"
                render={({ field: { value, onChange } }) => (
                  <TextField
                    size="medium"
                    fullWidth
                    placeholder="example@gamil.com"
                    error={!!errors.email?.message}
                    helperText={errors.email?.message}
                    value={value ?? ''}
                    onChange={handleChangeEmail(onChange)}
                  />
                )}
              />
            </S.EssentialInfo>
            <S.OptionalInfo>
              <div className="infoTitle">{t('Member_User_Bulk_Create_17')}</div>
              <S.InfoKey>{t('Member_User_Bulk_Create_18')}</S.InfoKey>
              <div className="groups">
                {dynamicGroups.map((group, idx) => {
                  return (
                    <div key={group.id} className="group-item">
                      <UserGroupSelect
                        selectedValue={{ name: group.name, userGroupId: group.userGroupId }}
                        onSelectChange={handleSeleted(idx)}
                        optionWidth={342}
                      />
                      <div className="add">
                        <Button
                          className="icon-btn"
                          variant="outline"
                          size="large"
                          onClick={() =>
                            add({ userGroupId: authStore.managerTenantInfo.id, name: authStore.managerTenantInfo.name })
                          }
                          disabled={dynamicGroups.length > idx + 1}
                        >
                          <AddIcon
                            width={20}
                            height={20}
                            color={
                              dynamicGroups.length > idx + 1
                                ? theme.colors['ic-gray-light']
                                : theme.colors['ic-gray-main']
                            }
                          />
                        </Button>
                      </div>
                      {idx > 0 && (
                        <div className="delete">
                          <Button className="icon-btn" variant="outline" size="large" onClick={() => remove(idx)}>
                            <DashIcon width={20} height={20} />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <S.InfoKey>{t('Member_User_16')}</S.InfoKey>
              <TextField
                {...register('phone')}
                size="medium"
                fullWidth
                placeholder={t('Member_User_Bulk_Create_19')}
                helperText={errors.phone?.message}
                error={!!errors.phone?.message}
                sx={{ marginBottom: '18px' }}
              />
              <S.InfoKey>{t('Member_User_Position')}</S.InfoKey>
              <TextField
                {...register('customRole')}
                size="medium"
                fullWidth
                placeholder={t('Member_User_Position_Placeholder')}
                helperText={errors.customRole?.message}
                error={!!errors.customRole?.message}
              />
              <Controller
                control={control}
                name="isMailSend"
                render={({ field }) => (
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '18px' }}>
                    <Checkbox
                      size="small"
                      icon={<CheckboxIcon color={theme.colors['ic-gray-lighter']} />}
                      checkedIcon={
                        <CheckboxIcon bgColor={theme.colors['bg-primary']} color={theme.colors['ic-white']} />
                      }
                      checked={field.value ?? true}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                    <span style={{ marginLeft: '8px', fontSize: '14px' }}>
                      {t('Member_User_Send_Invitation_Email')}
                    </span>
                  </div>
                )}
              />
            </S.OptionalInfo>
          </div>
          <ul className="description">
            <li>
              <BulletIcon width={6} height={6} color={theme.colors['ic-gray-lighter']} />
              {t('Member_User_Bulk_Create_20')}
            </li>
            <li>
              <BulletIcon width={6} height={6} color={theme.colors['ic-gray-lighter']} />
              {t('Member_User_Bulk_Create_21')}
            </li>
          </ul>
        </S.Body>
        <S.Footer $hasScrollbar={bodyHeight > 528}>
          <SecureSubmitButton isPending={isPending} variant="contain" size="extraLarge" fullWidth type="submit">
            {isMailSend ? t('Member_User_Bulk_Create_24') : t('Member_User_Invite')}
          </SecureSubmitButton>
        </S.Footer>
      </S.ModalWrapper>
    </Modal>
  );
});
